import { type AnyObject } from '@Types';
import { type QueryError } from './QueryError.types';

const NETWORK_ERROR_MESSAGE = 'A network error occurred.';
const TIMEOUT_ERROR_MESSAGE = 'The request timed out.';

type BuildQueryErrorArgs = {
    status: number;
    message: string;
    endpointPath: string;
    meta?: Record<string, unknown>;
};

export const buildQueryError = ({ status, message, endpointPath, meta = {} }: BuildQueryErrorArgs): QueryError => ({
    status,
    endpointPath,
    data: {
        message,
        meta,
    },
});

export async function formatTanStackErrorResponse(response: Response, endpointPath: string): Promise<QueryError> {
    const defaultMessage = 'An unknown error occurred.';
    let message = defaultMessage;
    let meta: Record<string, unknown> = {};

    try {
        const data = (await response.clone().json()) as AnyObject;
        const parsedMessage = data?.message;
        const parsedMeta = data?.meta;

        if (typeof parsedMessage === 'string' && parsedMessage.length > 0) {
            message = parsedMessage;
        }
        if (parsedMeta && typeof parsedMeta === 'object') {
            meta = parsedMeta as Record<string, unknown>;
        }
    } catch {
        // ignore JSON parsing errors and fall back to default message
    }

    return buildQueryError({
        status: response.status,
        message,
        meta,
        endpointPath,
    });
}

// This function triggers when `fetch` throws an error, which only happens from network-level errors.
// The error will be caught by the `retry` function configured in the `queryClient`, which will decide
// whether or not to retry.
export function formatTanStackFetchError(error: unknown, endpointPath: string): never {
    const abortCandidate = error as { name?: string };
    if (abortCandidate?.name === 'AbortError') {
        throw buildQueryError({
            status: 0,
            message: TIMEOUT_ERROR_MESSAGE,
            endpointPath,
        });
    }

    if (error instanceof Error) {
        throw buildQueryError({
            status: determineStatus(error),
            message: error.message || NETWORK_ERROR_MESSAGE,
            endpointPath,
        });
    }

    throw buildQueryError({
        status: 0,
        message: NETWORK_ERROR_MESSAGE,
        endpointPath,
    });
}

function determineStatus(error: Error): number {
    const candidate = error as { status?: unknown };
    if (typeof candidate.status === 'number') return candidate.status;

    return 0;
}
